import React, { useState } from "react";

import { CloseOutlined, InfoCircleOutlined, WarningFilled } from "@ant-design/icons";
import { Button, Select, Tooltip } from "antd";
import Modal from "antd/es/modal/Modal";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
  selectSearchSuffixIcon,
} from "../../Common/UIComponents/SelectPopupScrollbar";
import AddMemberModalInput from "./AddMemberModalInput";

import "./ChangeAdminModal.css";

const isSameEmail = (a = "", b = "") => a.trim().toLowerCase() === b.trim().toLowerCase();

const formatMember = (member) => (member.user_name ? `${member.user_email} (${member.user_name})` : member.user_email);

export default function ChangeAdminModal({
  members,
  currentAdmin,
  onConfirm,
  onClose,
}) {
  const [newAdminEmail, setNewAdminEmail] = useState(undefined);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const candidates = (members || []).filter(
    (member) => !isSameEmail(member.user_email, currentAdmin.user_email),
  );
  const newAdmin = candidates.find((member) => member.user_email === newAdminEmail);

  const currentAdminName = currentAdmin.user_name || currentAdmin.user_email;
  const newAdminName = newAdmin ? (newAdmin.user_name || newAdmin.user_email) : "";
  const dropdownOptions = candidates.map((member) => ({
    value: member.user_email,
    label: (
      <div className="ChangeAdminModal__Option">
        <span className="ChangeAdminModal__OptionEmail">{member.user_email}</span>
        {member.user_name && (
          <span className="ChangeAdminModal__OptionName">{member.user_name}</span>
        )}
      </div>
    ),
  }));

  const filterOption = (input, option) => {
    const member = candidates.find((m) => m.user_email === option.value);
    if (!member) return false;
    const search = input.trim().toLowerCase();
    return member.user_email.toLowerCase().includes(search)
      || (member.user_name || "").toLowerCase().includes(search);
  };

  /** Parent unmounts this modal on success, so only reset state on failure */
  const handleConfirm = async () => {
    setIsSubmitting(true);
    const success = await onConfirm(newAdmin);
    if (!success) {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <Modal
        open
        maskClosable={false}
        closable={false}
        centered
        footer={null}
        width="448px"
        wrapClassName="ChangeAdminModal"
        className="ChangeAdminModal"
      >
        <div className="ChangeAdminModal__Header">
          <div className="ChangeAdminModal__HeaderText">
            <span className="ChangeAdminModal__Title">Change Admin</span>
            <span className="ChangeAdminModal__Subtitle">
              Transfer admin rights to another team member
            </span>
          </div>
          <CloseOutlined
            className="ChangeAdminModal__CloseIcon"
            aria-label="Close"
            onClick={() => {
              if (!isSubmitting) {
                onClose();
              }
            }}
          />
        </div>

        <div className="ChangeAdminModal__Body">
          <div className="ChangeAdminModal__Alert">
            <WarningFilled className="ChangeAdminModal__AlertIcon" />
            <div className="ChangeAdminModal__AlertText">
              <span className="ChangeAdminModal__AlertTitle">
                Strict Account Policy: 1 Admin Limit
              </span>
              <span className="ChangeAdminModal__AlertBody">
                Transferring the Admin role will demote the current admin
                {" ("}
                <strong>{currentAdmin.user_email}</strong>
                {") to a "}
                <strong>Member</strong>
                .
              </span>
            </div>
          </div>

          <AddMemberModalInput
            label="Current Admin"
            value={formatMember(currentAdmin)}
            disabled
          />
          <div className="AddMemberModal__InputParent">
            <div className="myAccountFormTitle">
              New Admin
              {" "}
              <sup className="ChangeAdminModal__Required">*</sup>
              <Tooltip
                title="If you don't see the member in this dropdown, please invite them using the existing invite flow."
                placement="top"
                overlayClassName="ChangeAdminModal__Tooltip"
              >
                <InfoCircleOutlined
                  className="ChangeAdminModal__InfoIcon"
                  aria-label="More information"
                />
              </Tooltip>
            </div>
            <Select
              className="ChangeAdminModal__Select"
              placeholder="Search by email or name"
              value={newAdmin ? newAdminEmail : undefined}
              onChange={setNewAdminEmail}
              disabled={isSubmitting}
              options={dropdownOptions}
              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
              filterOption={filterOption}
              /* Keep ↓ after spread — AntD otherwise swaps to trailing search while open */
              suffixIcon={selectSearchSuffixIcon}
              classNames={selectPopupClassNames("ChangeAdminModal__Dropdown")}
              virtual={false}
              listHeight={256}
              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                optionCount: (dropdownOptions || []).length,
              })}
              notFoundContent="No team members found"
            />
          </div>
        </div>

        <div className="ChangeAdminModal__Footer">
          <Button
            type="default"
            onClick={() => onClose()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => setShowConfirm(true)}
            loading={isSubmitting}
            disabled={!newAdmin || isSubmitting}
          >
            Change Admin
          </Button>
        </div>
      </Modal>

      {showConfirm && newAdmin && (
        <Modal
          open
          centered
          maskClosable={false}
          closable={false}
          keyboard={!isSubmitting}
          footer={null}
          width={424}
          zIndex={1100}
          wrapClassName="ChangeAdminModal"
          className="ChangeAdminModal"
          onCancel={() => setShowConfirm(false)}
        >
          <div className="ChangeAdminModal__Header">
            <div className="ChangeAdminModal__HeaderText">
              <span className="ChangeAdminModal__Title">Change Admin Confirmation</span>
            </div>
          </div>

          <div className="ChangeAdminModal__Body">
            <p className="ChangeAdminModal__ConfirmText">
              Are you sure you want to transfer the account admin rights?
            </p>
            <p className="ChangeAdminModal__ConfirmText">
              <strong>{`${newAdminName} will become the new Admin, and ${currentAdminName} will become a Member.`}</strong>
            </p>
          </div>

          <div className="ChangeAdminModal__Footer">
            <Button
              type="default"
              onClick={() => setShowConfirm(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              loading={isSubmitting}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
